import { useCallback, useEffect, useState } from 'react';
import * as Sentry from '@sentry/react';

import {
  useSupabaseClient,
  useSupabaseReady,
} from '../supabase/SupabaseProvider';
import type { Conversation } from '../types';

export function useConversation(conversationId: string | null | undefined) {
  const client = useSupabaseClient();
  const ready = useSupabaseReady();
  const [conversation, setConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!client || !ready || !conversationId) {
      setConversation(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const { data, error: err } = await client
          .from('conversations')
          .select('*')
          .eq('id', conversationId)
          .maybeSingle();
        if (cancelled) return;
        if (err) {
          Sentry.captureException(err, {
            tags: { feature: 'comms', action: 'fetch_conversation' },
            extra: { conversation_id: conversationId },
          });
          setError(err.message);
          setConversation(null);
        } else {
          setConversation((data as Conversation | null) ?? null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [client, ready, conversationId]);

  // Realtime sync: when another admin closes/reopens the same chat, the
  // row UPDATE flows through the conversations publication and the chat
  // detail picks up the new close-state without a refetch.
  useEffect(() => {
    if (!client || !conversationId) return;
    const channelName = `conv-row:${conversationId}`;
    const existing = client
      .getChannels()
      .find((c) => c.topic === `realtime:${channelName}`);
    if (existing) void client.removeChannel(existing);

    const channel = client.channel(channelName).on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'conversations',
        filter: `id=eq.${conversationId}`,
      },
      (payload) => {
        const next = payload.new as Conversation;
        setConversation((prev) => (prev ? { ...prev, ...next } : next));
      },
    );
    channel.subscribe();
    return () => {
      void client.removeChannel(channel);
    };
  }, [client, conversationId]);

  // Local optimistic patch — close/reopen mutations call this on success
  // so the UI flips immediately, even before the realtime echo arrives.
  const patchConversation = useCallback((patch: Partial<Conversation>) => {
    setConversation((prev) => (prev ? { ...prev, ...patch } : prev));
  }, []);

  return { conversation, loading, error, patchConversation };
}
